/**
 * 100ViewsOnly fee ladder — locked rates (Task 11).
 *
 * Referral-based rates are one-way permanent (no decay).
 * A referral counts only when the referred creator is approved AND has
 * filled their first drop (100 views) — not on signup/approval alone.
 */

export const FOUNDING = 0.1;
export const ARCHITECT = 0.1; // 100 lifetime creator referrals — forever
export const KINGMAKER = 0.11; // 50 lifetime creator referrals — forever
export const RECRUITER = 0.12; // 15 lifetime creator referrals — forever
export const EARLY_WAVE = 0.1; // first 4 months from first drop, then → STANDARD
export const REFERRED = 0.13; // first 4 months from first drop, then → STANDARD
export const STANDARD = 0.15;

export const FOUNDING_SLOT_CAP = 25;

export const REFERRAL_THRESHOLDS = {
  recruiter: 15,
  kingmaker: 50,
  architect: 100,
};

/**
 * Creator Settings — 8-tier referral badge ladder
 * (locked names/thresholds from _reference/creator-settings.html).
 *
 * State rules:
 * - earned: lifetimeCreatorReferrals ≥ threshold
 * - current: the next unmet threshold (progress target)
 * - locked: everything after the current target
 *
 * Prefer reading users/{uid}.referralBadges written by Cloud Functions;
 * this helper is the shared algorithm used server-side (and for backfill).
 */
export const CREATOR_REFERRAL_BADGES = [
  {
    id: "first_call",
    label: "First Call",
    threshold: 1,
    threshLabel: "1",
    icon: "phone",
  },
  {
    id: "connector",
    label: "Connector",
    threshold: 3,
    threshLabel: "3",
    icon: "link",
  },
  {
    id: "talent_scout",
    label: "Talent Scout",
    threshold: 5,
    threshLabel: "5",
    icon: "search",
  },
  {
    id: "roster_builder",
    label: "Roster Builder",
    threshold: 10,
    threshLabel: "10",
    icon: "building",
  },
  {
    id: "founding_recruiter",
    label: "Founding Recruiter",
    threshold: 15,
    threshLabel: "15 · 12% locked",
    extra: "12% fee locked",
    icon: "mountain",
  },
  {
    id: "the_pipeline",
    label: "The Pipeline",
    threshold: 25,
    threshLabel: "25 · Pip outfit",
    extra: "Pip outfit unlock",
    icon: "pulse",
  },
  {
    id: "kingmaker",
    label: "Kingmaker",
    threshold: 50,
    threshLabel: "50 · 11%",
    extra: "11% fee locked",
    icon: "bolt",
  },
  {
    id: "architect",
    label: "The Architect",
    threshold: 100,
    threshLabel: "100 · 10% + announcement + plaque",
    extra: "10% fee · platform-wide announcement · physical plaque",
    icon: "home",
  },
];

/**
 * Compute badge ladder states from lifetimeCreatorReferrals.
 * current = next unmet threshold (not the highest earned).
 */
export function getCreatorReferralBadgeStates(lifetimeCreatorReferrals = 0) {
  const n = Math.max(0, Number(lifetimeCreatorReferrals) || 0);
  const nextUnmet = CREATOR_REFERRAL_BADGES.find((b) => n < b.threshold) || null;
  const currentId = nextUnmet?.id || null;

  const badges = CREATOR_REFERRAL_BADGES.map((badge) => {
    let state = "locked";
    if (n >= badge.threshold) state = "earned";
    else if (badge.id === currentId) state = "current";
    return { ...badge, state };
  });

  return {
    count: n,
    currentId,
    nextThreshold: nextUnmet?.threshold ?? null,
    nextLabel: nextUnmet?.label ?? null,
    badges,
  };
}

/** Compact payload stored on users/{uid}.referralBadges by Cloud Functions. */
export function buildReferralBadgesDoc(lifetimeCreatorReferrals = 0) {
  const computed = getCreatorReferralBadgeStates(lifetimeCreatorReferrals);
  return {
    count: computed.count,
    currentId: computed.currentId,
    nextThreshold: computed.nextThreshold,
    nextLabel: computed.nextLabel,
    badges: computed.badges.map((b) => ({
      id: b.id,
      label: b.label,
      threshold: b.threshold,
      threshLabel: b.threshLabel,
      extra: b.extra || null,
      icon: b.icon,
      state: b.state,
    })),
  };
}

const FOUR_MONTHS_MS = 1000 * 60 * 60 * 24 * 30 * 4;

/** Approval-time cohort preview (founding slots vs Early Wave). */
export function getRateIfApproved({ foundingSlotsRemaining }) {
  if (foundingSlotsRemaining > 0) {
    return {
      type: "founding",
      label: "✦ 10% forever",
      feeRate: FOUNDING,
      cohort: "founding",
    };
  }
  return {
    type: "earlywave",
    label: "Early Wave · 4mo",
    feeRate: EARLY_WAVE,
    afterWindowFeeRate: STANDARD,
    cohort: "earlywave",
  };
}

export function countFoundingApproved(applications = []) {
  return applications.filter(
    (app) => app.status === "approved" && app.feeTier === "founding"
  ).length;
}

export function foundingSlotsRemaining(applications = []) {
  return Math.max(0, FOUNDING_SLOT_CAP - countFoundingApproved(applications));
}

/** Permanent referral ladder — one-way, never decays. */
export function getReferralLadderRate(lifetimeCreatorReferrals = 0) {
  const n = Number(lifetimeCreatorReferrals) || 0;
  if (n >= REFERRAL_THRESHOLDS.architect) {
    return { tier: "architect", feeRate: ARCHITECT, label: "Architect · 10% forever" };
  }
  if (n >= REFERRAL_THRESHOLDS.kingmaker) {
    return { tier: "kingmaker", feeRate: KINGMAKER, label: "Kingmaker · 11% forever" };
  }
  if (n >= REFERRAL_THRESHOLDS.recruiter) {
    return { tier: "recruiter", feeRate: RECRUITER, label: "Founding Recruiter · 12% forever" };
  }
  return { tier: null, feeRate: null, label: null };
}

/**
 * Cohort base rate (ignores founding + referral ladder).
 * @param {"earlywave"|"referred"|"standard"|string} cohort
 * @param {Date|null} firstDropAt — start of 4-month window for Early Wave / Referred
 * @param {Date} [now]
 */
export function getCohortBaseRate(cohort, firstDropAt = null, now = new Date()) {
  const c = String(cohort || "standard").toLowerCase();

  // Window = 4 months from first drop. Before firstDropAt is set, keep the
  // introductory cohort rate (clock hasn't started).
  if (c === "earlywave") {
    if (!firstDropAt || now - toDate(firstDropAt) < FOUR_MONTHS_MS) {
      return { feeRate: EARLY_WAVE, label: "Early Wave · 10% (4mo window)" };
    }
    return { feeRate: STANDARD, label: "Standard · 15%" };
  }

  if (c === "referred") {
    if (!firstDropAt || now - toDate(firstDropAt) < FOUR_MONTHS_MS) {
      return { feeRate: REFERRED, label: "Referred · 13% (4mo window)" };
    }
    return { feeRate: STANDARD, label: "Standard · 15%" };
  }

  return { feeRate: STANDARD, label: "Standard · 15%" };
}

/**
 * Resolve the active platform fee for a creator.
 * Best (lowest) of founding / referral ladder / cohort base.
 */
export function resolveCreatorFeeRate({
  foundingCreator = false,
  lifetimeCreatorReferrals = 0,
  cohort = "standard",
  firstDropAt = null,
  now = new Date(),
} = {}) {
  if (foundingCreator) {
    return {
      feeRate: FOUNDING,
      source: "founding",
      label: "Founding · 10% forever",
      referralTier: getReferralLadderRate(lifetimeCreatorReferrals).tier,
      cohortRate: FOUNDING,
    };
  }

  const referral = getReferralLadderRate(lifetimeCreatorReferrals);
  const cohortBase = getCohortBaseRate(cohort, firstDropAt, now);

  if (referral.feeRate != null && referral.feeRate <= cohortBase.feeRate) {
    return {
      feeRate: referral.feeRate,
      source: referral.tier,
      label: referral.label,
      referralTier: referral.tier,
      cohortRate: cohortBase.feeRate,
    };
  }

  return {
    feeRate: cohortBase.feeRate,
    source: cohort || "standard",
    label: cohortBase.label,
    referralTier: referral.tier,
    cohortRate: cohortBase.feeRate,
  };
}

/**
 * Human-readable fee-tier card for /referral.
 * Always derived from resolveCreatorFeeRate — never a hardcoded preview.
 */
export function describeCreatorFeeStatus(user = {}) {
  const lifetime = Math.max(0, Number(user.lifetimeCreatorReferrals) || 0);
  const resolved = resolveCreatorFeeRate({
    foundingCreator: user.foundingCreator,
    lifetimeCreatorReferrals: lifetime,
    cohort: user.feeCohort || user.cohort,
    firstDropAt: user.firstDropAt,
  });
  const source = String(resolved.source || "standard").toLowerCase();
  const names = {
    founding: "Founding",
    architect: "The Architect",
    kingmaker: "Kingmaker",
    recruiter: "Recruiter, Locked",
    earlywave: "Early Wave",
    referred: "Referred",
    standard: "Standard",
  };
  const notes = {
    founding: "Locked forever — you don't need referrals to keep this rate.",
    architect: "100 lifetime referrals — the top tier, plus a platform-wide announcement and a physical plaque.",
    kingmaker: "One of the rarest tiers on the platform — 50 lifetime referrals.",
    recruiter: "Locked forever at 15 lifetime creator referrals.",
    earlywave: "Locked for your first 4 months from your first drop, then moves to 15%.",
    referred: "Locked for your first 4 months from your first drop since you joined via referral, then moves to 15%.",
    standard: "The baseline rate — refer creators to move up from here.",
  };

  return {
    name: names[source] || String(resolved.label || "Standard").split("·")[0].trim(),
    rate: `${Math.round(Number(resolved.feeRate) * 100)}%`,
    note: notes[source] || resolved.label,
    count: lifetime,
    feeRate: resolved.feeRate,
    source,
    label: resolved.label,
  };
}

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  return value instanceof Date ? value : new Date(value);
}
